// ═══════════════════════════════════════════════════════
// REBALANCING ENGINE — Drift calculation, alerts & trade suggestions
// ═══════════════════════════════════════════════════════
import { supabase } from "./core.js";
import { fetchAllLatestSnapshots } from "./snapshots.js";
import { fetchAllClientsMeta } from "./clientMeta.js";
import { createNotification } from "./notifications.js";
import { TARGET_ALLOCATION, ALLOC_CLASSES } from "../shared/constants.js";
import { normalizeAllocName, snapToData } from "../shared/dataUtils.js";
import { logWarn } from "../shared/logger.js";
import type { ClientMeta } from "../types/index.js";

// ─── Type definitions ───────────────────────────────────
interface DriftThresholds {
  ok: number;
  warning: number;
}

interface AllocationMap {
  [key: string]: number;
}

interface DriftEntry {
  id: string;
  label: string;
  color: string;
  current: number;
  target: number;
  driftPp: number;
  status: string;
}

interface TradeEntry {
  id: string;
  label: string;
  action: string;
  amount: number;
  driftPp: number;
  status: string;
}

interface DriftSummaryResult {
  ok: number;
  atencao: number;
  rebalancear: number;
  maxDrift: number;
  maxDriftClass: string;
}

interface ClientProfile {
  id: string;
  full_name: string;
  email: string;
}

interface SnapshotData {
  allocation: Array<{ name: string; value?: number; pct?: number }>;
  patrimonio: number;
}

interface ClientDriftOverview {
  clientId: string;
  clientName: string;
  code: string;
  email: string;
  patrimonio: number;
  healthScore: number;
  maxDrift: number;
  maxDriftClass: string;
  classesOk: number;
  classesAtencao: number;
  classesRebalancear: number;
  needsRebalancing: boolean;
  trades: TradeEntry[];
  driftDetails: DriftEntry[];
}

interface AlertedClient {
  clientId: string;
  clientName: string;
  maxDrift: number;
  maxDriftClass: string;
}

interface CheckAlertOptions {
  thresholdPp?: number;
  dryRun?: boolean;
}

// ─── Drift thresholds (percentage points) ────────────
export const DRIFT_THRESHOLDS = {
  ok: 5,
  warning: 10,
} as const satisfies DriftThresholds;

// ─── Status labels ───────────────────────────────────
export const DRIFT_STATUS = {
  OK: "OK",
  ATENCAO: "Atenção",
  REBALANCEAR: "Rebalancear",
} as const;

export function classifyDrift(driftPp: number): string {
  const abs = Math.abs(driftPp);
  if (abs <= DRIFT_THRESHOLDS.ok) return DRIFT_STATUS.OK;
  if (abs <= DRIFT_THRESHOLDS.warning) return DRIFT_STATUS.ATENCAO;
  return DRIFT_STATUS.REBALANCEAR;
}

export function extractCurrentAllocation(snapData: unknown): AllocationMap {
  const map: AllocationMap = {};
  const snap = snapData as
    | { allocation?: Array<{ name: string; value?: number; pct?: number }> }
    | undefined;
  (snap?.allocation || []).forEach((a) => {
    const id = normalizeAllocName(a.name);
    const pct = a.value ?? a.pct ?? 0;
    map[id] = (map[id] || 0) + pct;
  });
  return map;
}

export function calculateDrift(
  currentAlloc: AllocationMap,
  targetAlloc?: AllocationMap
): DriftEntry[] {
  const target = targetAlloc || TARGET_ALLOCATION;
  const allIds = new Set([...Object.keys(target), ...Object.keys(currentAlloc)]);
  const classOrder = ALLOC_CLASSES.map((a) => a.id);
  const classMap: { [key: string]: { id: string; label: string; cor?: string } } = {};
  ALLOC_CLASSES.forEach((a) => {
    classMap[a.id] = a;
  });

  return classOrder
    .filter((id) => allIds.has(id))
    .map((id) => {
      const current = (currentAlloc as Record<string, number>)[id] || 0;
      const tgt = (target as Record<string, number>)[id] || 0;
      const driftPp = current - tgt;
      const cls = classMap[id] || {};
      return {
        id,
        label: cls.label || id,
        color: cls.cor || "#64748B",
        current,
        target: tgt,
        driftPp,
        status: classifyDrift(driftPp),
      };
    });
}

export function suggestTrades(
  currentAlloc: AllocationMap,
  targetAlloc: AllocationMap | undefined,
  patrimonio: number
): TradeEntry[] {
  const drift = calculateDrift(currentAlloc, targetAlloc);
  return drift
    .filter((d) => Math.abs(d.driftPp) > DRIFT_THRESHOLDS.ok)
    .map((d) => {
      const valorAtual = (patrimonio * d.current) / 100;
      const valorIdeal = (patrimonio * d.target) / 100;
      const amount = valorIdeal - valorAtual;
      return {
        id: d.id,
        label: d.label,
        action: amount > 0 ? "Comprar" : "Vender",
        amount: Math.abs(amount),
        driftPp: d.driftPp,
        status: d.status,
      };
    })
    .sort((a, b) => b.amount - a.amount);
}

export function portfolioHealthScore(
  currentAlloc: AllocationMap,
  targetAlloc?: AllocationMap
): number {
  const drift = calculateDrift(currentAlloc, targetAlloc);
  const totalAbsDrift = drift.reduce((sum, d) => sum + Math.abs(d.driftPp), 0);
  return Math.max(0, Math.round(100 - totalAbsDrift));
}

export function needsRebalancing(
  currentAlloc: AllocationMap,
  targetAlloc?: AllocationMap
): boolean {
  const drift = calculateDrift(currentAlloc, targetAlloc);
  return drift.some((d) => Math.abs(d.driftPp) > DRIFT_THRESHOLDS.ok);
}

export function driftSummary(
  currentAlloc: AllocationMap,
  targetAlloc?: AllocationMap
): DriftSummaryResult {
  const drift = calculateDrift(currentAlloc, targetAlloc);
  const summary: DriftSummaryResult = {
    ok: 0,
    atencao: 0,
    rebalancear: 0,
    maxDrift: 0,
    maxDriftClass: "",
  };
  drift.forEach((d) => {
    const abs = Math.abs(d.driftPp);
    if (d.status === DRIFT_STATUS.OK) summary.ok++;
    else if (d.status === DRIFT_STATUS.ATENCAO) summary.atencao++;
    else summary.rebalancear++;
    if (abs > summary.maxDrift) {
      summary.maxDrift = abs;
      summary.maxDriftClass = d.label;
    }
  });
  return summary;
}

// ═══════════════════════════════════════════════════════
// ADVISOR — Aggregate drift data across all clients
// ═══════════════════════════════════════════════════════

export async function fetchAllClientsDriftOverview(): Promise<ClientDriftOverview[]> {
  try {
    const [snapshots, metas] = await Promise.all([
      fetchAllLatestSnapshots(),
      fetchAllClientsMeta(),
    ]);

    const { data: profiles } = (await supabase.from("profiles").select("id, full_name, email")) as {
      data: ClientProfile[] | null;
    };

    const profileMap: { [key: string]: ClientProfile } = {};
    (profiles || []).forEach((p) => {
      profileMap[p.id] = p;
    });

    const metaMap: { [key: string]: ClientMeta } = {};
    (metas || []).forEach((m) => {
      metaMap[m.client_id] = m;
    });

    const results: ClientDriftOverview[] = [];
    for (const snap of snapshots || []) {
      const clientId = snap.client_id as string;
      const data = snapToData(snap) as SnapshotData;
      const meta = metaMap[clientId];
      const profile = profileMap[clientId];
      const targetAlloc = meta?.target_allocation || TARGET_ALLOCATION;
      const currentAlloc = extractCurrentAllocation(data);
      const patrimonio = data?.patrimonio || 0;
      const allocToUse = targetAlloc ? (targetAlloc as AllocationMap) : undefined;

      const drift = calculateDrift(currentAlloc, allocToUse);
      const health = portfolioHealthScore(currentAlloc, allocToUse);
      const summary = driftSummary(currentAlloc, allocToUse);
      const trades = suggestTrades(currentAlloc, allocToUse, patrimonio);

      results.push({
        clientId,
        clientName: profile?.full_name || meta?.code || clientId.slice(0, 8),
        code: meta?.code || "",
        email: profile?.email || "",
        patrimonio,
        healthScore: health,
        maxDrift: summary.maxDrift,
        maxDriftClass: summary.maxDriftClass,
        classesOk: summary.ok,
        classesAtencao: summary.atencao,
        classesRebalancear: summary.rebalancear,
        needsRebalancing: summary.atencao + summary.rebalancear > 0,
        trades,
        driftDetails: drift,
      });
    }

    results.sort((a, b) => a.healthScore - b.healthScore);
    return results;
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    logWarn("rebalancing", error.message, { context: "fetchAllClientsDriftOverview" });
    return [];
  }
}

// ═══════════════════════════════════════════════════════
// DRIFT ALERTS — Create notifications for out-of-target clients
// ═══════════════════════════════════════════════════════

export async function checkAndAlertDriftAll(
  options: CheckAlertOptions = {}
): Promise<AlertedClient[]> {
  const { thresholdPp = DRIFT_THRESHOLDS.ok, dryRun = false } = options;
  const overview = await fetchAllClientsDriftOverview();
  const alerted: AlertedClient[] = [];

  for (const client of overview) {
    if (client.maxDrift > thresholdPp) {
      if (!dryRun) {
        try {
          await createNotification(
            client.clientId,
            "drift_alert",
            `⚠️ Carteira fora do alvo — ${client.maxDriftClass} (${client.maxDrift.toFixed(1)}pp)`,
            `Sua alocação em ${client.maxDriftClass} está ${client.maxDrift.toFixed(1)} pontos percentuais acima/abaixo do alvo. Converse com seu assessor sobre rebalanceamento.`
          );
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          logWarn("rebalancing", error.message, {
            context: "alertDrift",
            clientId: client.clientId,
          });
        }
      }
      alerted.push({
        clientId: client.clientId,
        clientName: client.clientName,
        maxDrift: client.maxDrift,
        maxDriftClass: client.maxDriftClass,
      });
    }
  }
  return alerted;
}
